import { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  actions as authActions,
  selectors as authSelectors,
} from 'services/auth';
import config from 'config';

/**
 * Custom hook to handle automatic logout after a period of inactivity
 *
 * Features:
 * - Tracks user activity (mouse, keyboard, scroll, touch events)
 * - Shows warning notification 1 minute before logout
 * - Automatically logs out user after 30 minutes of inactivity
 * - Handles cleanup for Pro Santé Connect sessions
 *
 * Performance considerations:
 * - Passive event listeners for better scroll/touch performance
 * - RequestAnimationFrame-based throttling for smoother performance
 * - Refs to avoid unnecessary re-renders and useEffect triggers
 * - Regular functions instead of useCallback where appropriate
 *
 * @param inactivityTimeout - Time in milliseconds before logout (default: 30 minutes)
 * @param warningTimeout - Time in milliseconds before showing warning (default: 29 minutes)
 */
const useInactivityTimer = (
  inactivityTimeout: number = 30 * 60 * 1000, // 30 minutes
  warningTimeout: number = 29 * 60 * 1000 // 29 minutes
) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(authSelectors.isAuthenticated);
  const authState = useSelector((state: any) => state.auth);

  const [showWarning, setShowWarning] = useState(false);

  // Refs for timer management
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const rafIdRef = useRef<number | null>(null);
  const isThrottledRef = useRef<boolean>(false);

  // Ref for resetTimer - must be ref because it's called from stable handleActivity callback
  const resetTimerRef = useRef<() => void>();

  /**
   * Clears all timers - regular function, only uses refs
   */
  const clearTimers = () => {
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
  };

  /**
   * Handles the logout process - regular function, uses closure
   * - For Pro Santé Connect sessions, calls the PSC logout endpoint
   * - Dispatches the logout action to clear Redux state
   * - Uses full page reload for clean state after inactivity timeout
   */
  const handleAutoLogout = () => {
    clearTimers();
    setShowWarning(false);

    const performLogout = () => {
      dispatch(authActions.logout());

      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    };

    // Check if this is a Pro Santé Connect session
    if (authState.session?.isPSC) {
      fetch(
        config.PRO_SANTE_CONNECT_AUTH_HOST +
          'realms/esante-wallet/protocol/openid-connect/logout?id_token_hint=' +
          authState.session.idToken
      )
        .then(performLogout)
        .catch((err) => {
          console.error(
            'PSC logout failed, proceeding with local logout:',
            err
          );
          performLogout();
        });
    } else {
      performLogout();
    }
  };

  /**
   * Update resetTimer ref when dependencies change
   * Uses ref to avoid triggering useEffect re-runs
   */
  useEffect(() => {
    resetTimerRef.current = () => {
      if (!isAuthenticated) {
        return;
      }

      clearTimers();
      setShowWarning(false);

      // Set warning timer (shows warning 1 minute before logout)
      warningTimerRef.current = setTimeout(() => {
        setShowWarning(true);
      }, warningTimeout);

      // Set inactivity timer (logs out after full timeout)
      inactivityTimerRef.current = setTimeout(() => {
        handleAutoLogout();
      }, inactivityTimeout);
    };
  }, [isAuthenticated, inactivityTimeout, warningTimeout]);

  /**
   * Dismisses the warning and resets the timer
   * useCallback because it's part of the public API
   */
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    resetTimerRef.current?.();
  }, []);

  /**
   * Activity event handler with requestAnimationFrame-based throttling
   * useCallback because it's passed to addEventListener
   */
  const handleActivity = useCallback(() => {
    if (isThrottledRef.current) {
      return;
    }

    isThrottledRef.current = true;

    rafIdRef.current = requestAnimationFrame(() => {
      resetTimerRef.current?.();

      // Reset throttle after 1 second
      setTimeout(() => {
        isThrottledRef.current = false;
      }, 1000);
    });
  }, []);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) {
      clearTimers();
      setShowWarning(false);
      return;
    }

    // Events that need to be able to preventDefault (active listeners)
    const activeEvents = ['mousedown', 'keydown', 'touchstart', 'click'];
    // Events that only read data and can be optimized (passive listeners)
    const passiveEvents = ['scroll', 'wheel'];

    // Add active event listeners
    activeEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Add passive event listeners for better scroll/wheel performance
    passiveEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initialize the timer
    resetTimerRef.current?.();

    // Cleanup function
    return () => {
      activeEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      passiveEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [isAuthenticated, handleActivity]);

  return {
    showWarning,
    dismissWarning,
    resetTimer: useCallback(() => resetTimerRef.current?.(), []),
  };
};

export default useInactivityTimer;
